<!-- eslint-disable vue/multi-word-component-names -->
<template>
  <div ref="rootEl" class="confetti">
    <canvas ref="canvasEl" class="confetti__canvas" :style="canvasStyle" />
  </div>
</template>

<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import { PipeDisplayMode, usePipeIfAvailable } from '@render/composables/usePipe'
import { useComponentProcessors } from '@render/composables/useComponentProcessors'
import { useEditorCommands } from '@render/composables/useEditorCommands'
import { getColorStyleData } from '@render/composables/useStyleData'
import type { ColorField } from '@render/types/styles'
import type { ConfettiFields as Model } from './definition'

type Shape = 'circle' | 'triangle' | 'square' | 'rectangle' | 'heart' | 'star'

const fields = defineModel<Model>('fields', {
  required: true,
  default: (): Model => ({}),
})

const emit = defineEmits<{
  (e: 'onComplete'): void
}>()

const pipe = usePipeIfAvailable()
const isEditor = computed(() => pipe?.currentMode.value === PipeDisplayMode.EDITOR)

const rootEl = ref<HTMLDivElement | null>(null)
const canvasEl = ref<HTMLCanvasElement | null>(null)

// Origin of the burst, relative to the canvas. Exposed as refs so the
// origin can be repositioned in code later (e.g. to the cursor or a
// clicked element) without touching the rendering pipeline.
const originX = ref<number | null>(null)
const originY = ref<number | null>(null)

const canvasStyle = computed(() => {
  const raw = Number(fields.value.opacity ?? 1)
  const clamped = Math.max(0, Math.min(1, Number.isFinite(raw) ? raw : 1))
  return { opacity: String(clamped) }
})

const enabledShapes = computed<Shape[]>(() => {
  const shapes: Shape[] = []
  if (fields.value.shapeCircle !== false) shapes.push('circle')
  if (fields.value.shapeTriangle !== false) shapes.push('triangle')
  if (fields.value.shapeSquare !== false) shapes.push('square')
  if (fields.value.shapeRectangle !== false) shapes.push('rectangle')
  if (fields.value.shapeHeart !== false) shapes.push('heart')
  if (fields.value.shapeStar !== false) shapes.push('star')
  return shapes.length > 0 ? shapes : ['circle']
})

// Fallback palette used when none of the five colour fields are set, so
// pieces never render invisibly.
const FALLBACK_PALETTE = ['#FF4D6D', '#FFBC42', '#06D6A0', '#118AB2', '#9D4EDD']

const rawPalette = computed<string[]>(() => {
  const entries: (ColorField | undefined)[] = [
    fields.value.color1,
    fields.value.color2,
    fields.value.color3,
    fields.value.color4,
    fields.value.color5,
  ]
  const resolved = entries.map((c) => getColorStyleData(c)).filter((c): c is string => !!c)
  return resolved.length > 0 ? resolved : FALLBACK_PALETTE
})

// Resolve any `var(--…)` entries into concrete rgb strings at burst time,
// since canvas fillStyle can't consume CSS custom properties directly.
function resolvePalette(el: HTMLElement): string[] {
  return rawPalette.value.map((value) => {
    if (!value.startsWith('var(')) return value
    const probe = document.createElement('span')
    probe.style.color = value
    probe.style.display = 'none'
    el.appendChild(probe)
    const resolved = getComputedStyle(probe).color
    probe.remove()
    return resolved || '#FF4D6D'
  })
}

interface Particle {
  x: number
  y: number
  vx: number
  vy: number
  angleRad: number
  angularVelRad: number
  size: number
  shape: Shape
  color: string
  fadeDelayMs: number
  fadeDurationMs: number
  elapsedMs: number
  alpha: number
}

let particles: Particle[] = []
let rafId: number | null = null
let lastFrameTs = 0

function rand(min: number, max: number) {
  return min + Math.random() * (max - min)
}

function spawnParticles() {
  const canvas = canvasEl.value
  if (!canvas) return
  const palette = resolvePalette(canvas)
  const shapes = enabledShapes.value

  const pieceCount = Math.max(0, Math.floor(Number(fields.value.pieceCount ?? 120)))
  const minRadius = Math.max(0, Number(fields.value.minRadiusPx ?? 80))
  const maxRadius = Math.max(minRadius, Number(fields.value.maxRadiusPx ?? 260))
  const burstDurationSec = Math.max(0.05, Number(fields.value.burstDurationMs ?? 450) / 1000)
  const minSize = Math.max(1, Number(fields.value.minSizePx ?? 8))
  const maxSize = Math.max(minSize, Number(fields.value.maxSizePx ?? 16))
  const minFadeDelay = Math.max(0, Number(fields.value.minFadeDelayMs ?? 700))
  const maxFadeDelay = Math.max(minFadeDelay, Number(fields.value.maxFadeDelayMs ?? 2400))
  const rotMaxDegPerSec = Math.max(0, Number(fields.value.rotationSpeedDegPerSec ?? 540))

  const rawPct = Number(fields.value.originYPercent ?? 50)
  const pct = Math.max(0, Math.min(100, Number.isFinite(rawPct) ? rawPct : 50))
  const cx = originX.value ?? canvas.clientWidth / 2
  const cy = originY.value ?? (canvas.clientHeight * pct) / 100

  const next: Particle[] = []
  for (let i = 0; i < pieceCount; i++) {
    const angle = rand(0, Math.PI * 2)
    const targetRadius = rand(minRadius, maxRadius)
    const speed = targetRadius / burstDurationSec
    next.push({
      x: cx,
      y: cy,
      vx: Math.cos(angle) * speed,
      vy: Math.sin(angle) * speed,
      angleRad: rand(0, Math.PI * 2),
      angularVelRad: rand(-rotMaxDegPerSec, rotMaxDegPerSec) * (Math.PI / 180),
      size: rand(minSize, maxSize),
      shape: shapes[Math.floor(Math.random() * shapes.length)]!,
      color: palette[Math.floor(Math.random() * palette.length)]!,
      fadeDelayMs: rand(minFadeDelay, maxFadeDelay),
      fadeDurationMs: Math.max(1, Number(fields.value.fadeDurationMs ?? 900)),
      elapsedMs: 0,
      alpha: 1,
    })
  }
  particles = next
}

function step(ts: number) {
  if (!canvasEl.value) return
  if (lastFrameTs === 0) lastFrameTs = ts
  const dtMs = Math.min(64, ts - lastFrameTs)
  lastFrameTs = ts
  const dt = dtMs / 1000

  const gravity = Number(fields.value.gravityPxPerSec2 ?? 1400)
  const dragBase = Math.min(1, Math.max(0, Number(fields.value.drag ?? 0.985)))
  // Per-frame drag coefficient is authored relative to 60fps — scale to the
  // actual frame delta so motion stays consistent on high-refresh displays.
  const dragFactor = Math.pow(dragBase, dt * 60)

  let alive = 0
  for (const p of particles) {
    p.elapsedMs += dtMs
    p.vy += gravity * dt
    p.vx *= dragFactor
    p.vy *= dragFactor
    p.x += p.vx * dt
    p.y += p.vy * dt
    p.angleRad += p.angularVelRad * dt
    if (p.elapsedMs > p.fadeDelayMs) {
      const fadeProgress = (p.elapsedMs - p.fadeDelayMs) / p.fadeDurationMs
      p.alpha = Math.max(0, 1 - fadeProgress)
    }
    if (p.alpha > 0) alive++
  }

  draw()

  if (alive > 0) {
    rafId = requestAnimationFrame(step)
  } else {
    stop()
    if (!isEditor.value) emit('onComplete')
  }
}

function draw() {
  const canvas = canvasEl.value
  if (!canvas) return
  const ctx = canvas.getContext('2d')
  if (!ctx) return
  const dpr = window.devicePixelRatio || 1
  ctx.setTransform(dpr, 0, 0, dpr, 0, 0)
  ctx.clearRect(0, 0, canvas.clientWidth, canvas.clientHeight)

  for (const p of particles) {
    if (p.alpha <= 0) continue
    ctx.save()
    ctx.globalAlpha = p.alpha
    ctx.fillStyle = p.color
    ctx.translate(p.x, p.y)
    ctx.rotate(p.angleRad)
    drawShape(ctx, p.shape, p.size)
    ctx.restore()
  }
}

function drawShape(ctx: CanvasRenderingContext2D, shape: Shape, size: number) {
  const s = size
  ctx.beginPath()
  switch (shape) {
    case 'circle': {
      ctx.arc(0, 0, s / 2, 0, Math.PI * 2)
      break
    }
    case 'square': {
      ctx.rect(-s / 2, -s / 2, s, s)
      break
    }
    case 'rectangle': {
      const w = s
      const h = s * 0.45
      ctx.rect(-w / 2, -h / 2, w, h)
      break
    }
    case 'triangle': {
      const h = (s * Math.sqrt(3)) / 2
      ctx.moveTo(0, -h / 2)
      ctx.lineTo(s / 2, h / 2)
      ctx.lineTo(-s / 2, h / 2)
      ctx.closePath()
      break
    }
    case 'heart': {
      // Simple two-lobe heart, inscribed in a box of side `s` and centred
      // on (0, 0). The cusp sits at y = -s/4, the tip at y = s/2.
      const w = s
      const h = s
      ctx.moveTo(0, -h / 4)
      ctx.bezierCurveTo(w / 2, -h / 2, w / 2, h / 6, 0, h / 2)
      ctx.bezierCurveTo(-w / 2, h / 6, -w / 2, -h / 2, 0, -h / 4)
      break
    }
    case 'star': {
      const spikes = 5
      const outer = s / 2
      const inner = outer * 0.45
      let rot = -Math.PI / 2
      const step = Math.PI / spikes
      ctx.moveTo(Math.cos(rot) * outer, Math.sin(rot) * outer)
      for (let i = 0; i < spikes; i++) {
        rot += step
        ctx.lineTo(Math.cos(rot) * inner, Math.sin(rot) * inner)
        rot += step
        ctx.lineTo(Math.cos(rot) * outer, Math.sin(rot) * outer)
      }
      ctx.closePath()
      break
    }
  }
  ctx.fill()
}

function resizeCanvas() {
  const canvas = canvasEl.value
  if (!canvas) return
  const dpr = window.devicePixelRatio || 1
  const w = Math.max(1, Math.round(canvas.clientWidth * dpr))
  const h = Math.max(1, Math.round(canvas.clientHeight * dpr))
  if (canvas.width !== w) canvas.width = w
  if (canvas.height !== h) canvas.height = h
}

function burst() {
  resizeCanvas()
  spawnParticles()
  if (rafId === null) {
    lastFrameTs = 0
    rafId = requestAnimationFrame(step)
  }
}

function stop() {
  if (rafId !== null) {
    cancelAnimationFrame(rafId)
    rafId = null
  }
  particles = []
  const canvas = canvasEl.value
  const ctx = canvas?.getContext('2d')
  if (canvas && ctx) {
    ctx.clearRect(0, 0, canvas.width, canvas.height)
  }
}

let resizeObserver: ResizeObserver | null = null

onMounted(() => {
  resizeCanvas()
  if (typeof ResizeObserver !== 'undefined' && canvasEl.value) {
    resizeObserver = new ResizeObserver(() => resizeCanvas())
    resizeObserver.observe(canvasEl.value)
  }
  if (!isEditor.value && fields.value.autoPlay !== false) {
    burst()
  }
})

onBeforeUnmount(() => {
  stop()
  if (resizeObserver) {
    resizeObserver.disconnect()
    resizeObserver = null
  }
})

// Re-burst on mode flip into non-editor so preview→live transitions in the
// builder show the animation immediately rather than waiting for a reload.
watch(isEditor, (nowEditor) => {
  if (!nowEditor && fields.value.autoPlay !== false) {
    burst()
  } else if (nowEditor) {
    stop()
  }
})

useComponentProcessors({
  burst: () => burst(),
  stop: () => stop(),
})

useEditorCommands({
  previewAnimation: () => burst(),
})
</script>

<style scoped lang="scss">
// Fullscreen overlay — out of flow so the component takes no layout space
// in its parent, and click-through so pieces never block interaction with
// the page underneath. Same shape as PageBackground.
.confetti {
  position: fixed;
  inset: 0;
  pointer-events: none;
  z-index: 9999;
}

.confetti__canvas {
  width: 100%;
  height: 100%;
  display: block;
  pointer-events: none;
}
</style>
